import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface AnimatedCounterProps {
  value: string;
  className?: string;
  duration?: number;
}

function parseValue(value: string): { number: number; suffix: string } {
  const match = value.match(/^(\d+)(.*)$/);
  if (match && match[1]) {
    return {
      number: parseInt(match[1], 10),
      suffix: match[2] ?? '',
    };
  }
  return { number: 0, suffix: value };
}

export function AnimatedCounter({ value, className = '', duration = 2 }: AnimatedCounterProps) {
  const counterRef = useRef<HTMLSpanElement>(null);
  const [displayValue, setDisplayValue] = useState('0');
  const { number: targetNumber, suffix } = parseValue(value);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!counterRef.current || hasAnimated.current) return;

    const ctx = gsap.context(() => {
      const counter = { value: 0 };

      ScrollTrigger.create({
        trigger: counterRef.current,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          if (hasAnimated.current) return;
          hasAnimated.current = true;

          gsap.to(counter, {
            value: targetNumber,
            duration,
            ease: 'power2.out',
            onUpdate: () => {
              setDisplayValue(Math.round(counter.value).toString());
            },
          });
        },
      });
    }, counterRef);

    return () => ctx.revert();
  }, [targetNumber, duration]);

  return (
    <span ref={counterRef} className={className}>
      {displayValue}
      {suffix}
    </span>
  );
}
